import { parse, format, parseISO, isValid } from 'date-fns'

interface User {
  profile?: {
    dateFormat?: string
  }
}

interface FieldConfig {
  default?: string | Date
}

type DateValue = Date | string | number | null | undefined

const PARSE_FORMATS = ['MM/dd/yyyy', 'yyyy-MM-dd', 'dd/MM/yyyy', 'yyyy-dd-MM']

const DATE_FORMAT_MAP: Record<string, string> = {
  'MM/DD/YYYY': 'MM/dd/yyyy',
  'DD/MM/YYYY': 'dd/MM/yyyy',
  'YYYY-MM-DD': 'yyyy-MM-dd',
  'YYYY-DD-MM': 'yyyy-dd-MM',
}

const DEFAULT_FORMAT = 'MM/dd/yyyy'

/**
 * Converts any date value to a Date object for consistent internal handling
 * @param value - Date value (Date object, string, etc.)
 * @returns Date object or null if invalid
 */
export const toDateObject = (value: DateValue): Date | null => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  if (value instanceof Date) {
    return isValid(value) ? value : null
  }

  if (typeof value === 'number') {
    const numericDate = new Date(value)
    return isValid(numericDate) ? numericDate : null
  }

  if (typeof value === 'string') {
    const isoDate = parseISO(value)
    if (isValid(isoDate)) return isoDate

    for (const fmt of PARSE_FORMATS) {
      const parsed = parse(value, fmt, new Date())
      if (isValid(parsed)) return parsed
    }

    const fallback = new Date(value)
    if (isValid(fallback)) return fallback
  }

  return null
}

/**
 * Formats a date for display using user preferences
 * @param value - Date value
 * @param user - User object with profile preferences
 * @returns Formatted date string
 */
export const formatDateForDisplay = (
  value: DateValue,
  user: User | null = null
): string => {
  if (value === null || value === undefined || value === '') return ''

  const dateObj = toDateObject(value)
  if (!dateObj) {
    return String(value)
  }

  const userFormat = user?.profile?.dateFormat?.toUpperCase()
  const dateFnsFormat =
    (userFormat && DATE_FORMAT_MAP[userFormat]) ?? DEFAULT_FORMAT

  return format(dateObj, dateFnsFormat)
}

/**
 * Converts date to ISO string for API calls
 * @param value - Date value
 * @returns ISO string or original value if can't convert
 */
export const toApiFormat = (value: DateValue): string | DateValue => {
  if (value === null || value === undefined) {
    return value
  }

  const dateObj = toDateObject(value)
  if (dateObj) {
    return dateObj.toISOString()
  }

  return value
}

/**
 * Converts date to timestamp for Mapbox expressions
 * @param dateValue - Date value
 * @returns Timestamp or null
 */
export const toTimestamp = (dateValue: DateValue): number | null => {
  if (dateValue === null || dateValue === undefined || dateValue === '')
    return null

  const dateObj = toDateObject(dateValue)
  if (dateObj) {
    return dateObj.getTime()
  }

  return null
}

/**
 * Processes server row data to ensure consistent Date objects
 * @param value - Raw value from server
 * @param fieldConfig - Field configuration with default value
 * @returns Processed value
 */
export const processServerDateValue = (
  value: DateValue,
  fieldConfig: FieldConfig = {}
): Date | string | null => {
  // Handle empty/null values with defaults
  if (value === null || value === '' || value === undefined) {
    if (fieldConfig.default && fieldConfig.default !== '') {
      const defaultDate = toDateObject(fieldConfig.default)
      return defaultDate ?? fieldConfig.default
    }
    return null
  }

  // Convert to Date object for consistent handling
  return toDateObject(value)
}
